<template>
  <div v-if="!loading">
    <b-row class="flex-row justify-content-between align-items-start">
      <b-col>
        <h2 class="font-weight-bold text-dark">
          {{ $t('quote') }}
          #{{ quote.folio }}
        </h2>
        <span class="font-weight-bolder">
          {{ $t('updatedPrices') }} {{ new Date().getDate() }}/{{
            new Date().getMonth() + 1
          }}/{{ new Date().getFullYear() }}
        </span>
        <p class="text-muted">
          {{ $t('imageInfo') }}
        </p>
      </b-col>
      <b-col class="text-right">
        <b-button
          variant="primary"
          @click="copyClientLink"
        >
          <feather-icon
            icon="LinkIcon"
            size="16"
            class="mr-50"
          />
          {{ $t('copyClientLink') }}
        </b-button>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col cols="12">
        <b-card class="quote-visibility-card">
          <div class="d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h4 class="mb-25">
                Visibilidad para el cliente
              </h4>
              <p class="text-muted mb-1">
                Cotización #{{ quote.customer_quote_position }} de este cliente.
                Nació con el switch que el cliente tenía en ese momento y ya no cambia sola.
                <template v-if="quote.open_quotation_overridden">
                  Un admin fijó este valor.
                </template>
              </p>
            </div>
            <b-badge
              :variant="quote.open_quotation_enabled ? 'light-success' : 'light-secondary'"
              class="quote-visibility-card__badge"
            >
              {{ quote.open_quotation_enabled ? 'ABIERTA' : 'CERRADA' }}
            </b-badge>
          </div>

          <b-form-radio-group
            v-model="openQuotationChoice"
            buttons
            button-variant="outline-primary"
            :disabled="savingOpenQuotation"
            :options="openQuotationOptions"
            @change="saveOpenQuotation"
          />

          <p class="text-muted mb-0 mt-1">
            <small>
              Cerrada: el cliente ve la comparativa completa pero los proveedores salen como
              &quot;Proveedor 1&quot;, &quot;Proveedor 2&quot;, sin nombre ni teléfono.
            </small>
          </p>
        </b-card>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col
        v-if="showCloserTraceability"
        cols="12"
      >
        <b-card>
          <h4 class="mb-1">
            {{ $t('quoteCloserTraceabilityTitle') }}
          </h4>
          <b-row>
            <b-col md="4" sm="12" class="mb-1 mb-md-0">
              <small class="text-muted d-block">{{ $t('quoteCloserLabel') }}</small>
              <strong>{{ quote.quote_closer_name || $t('notAvailable') }}</strong>
            </b-col>
            <b-col md="4" sm="12" class="mb-1 mb-md-0">
              <small class="text-muted d-block">{{ $t('quoteCloserTypeLabel') }}</small>
              <strong>{{ closerOriginLabel }}</strong>
            </b-col>
            <b-col md="4" sm="12">
              <small class="text-muted d-block">{{ $t('quoteClosedAtLabel') }}</small>
              <strong>{{ closerClosedAtLabel }}</strong>
            </b-col>
          </b-row>
          <div v-if="quote.quote_closer_email" class="mt-1">
            <small class="text-muted d-block">{{ $t('email') }}</small>
            <strong>{{ quote.quote_closer_email }}</strong>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col>
        <b-card no-body>
          <quotes-table
            :quotes="[{ ...quote }]"
            :fields="quoteFields"
            no-tools
          >
            <template #cell(address)="data">
              {{ data.value.city }}
            </template>
          </quotes-table>
        </b-card>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col>
        <commission-suggestion-panel
          :quote="quote"
          :quote-id="$route.params.id"
          @applied="fetchQuoteMetadata($route.params.id)"
        />
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <!-- <b-col>
        <client-pricing-list
          :quote="quote"
          :products="quote.quote_items_attributes"
        />
      </b-col> -->
    </b-row>
    <b-row class="">
      <b-col sm="12">
        <b-card
          class="overflow-hidden"
          no-body
        >
          <div class="comparative-toolbar">
            <div>
              <h4 class="mb-0">
                Comparativa de precios
              </h4>
              <small class="text-muted">
                Proveedores ordenados por índice de precio: 1.00 es haber dado el precio más
                bajo en todo lo que cotizó.
              </small>
            </div>
          </div>

          <b-table
            :no-border-collapse="true"
            responsive
            sticky-header="70vh"
            :items="metadataItems"
            :fields="metadataFields"
            :tbody-tr-class="() => 'comparative-row'"
            class="mb-0 comparative-prices-table"
          >
            <!-- We are using utility class `text-nowrap` to help illustrate horizontal scrolling -->
            <template #head(supplier)="row">
              <div class="text-nowrap">
                {{ row.label }}
              </div>
            </template>
            <template #cell()="data">
              <template v-if="data.field.key === 'supplier'">
                <div class="supplier-cell">
                  {{ data.value }}
                </div>
                <div
                  v-if="data.item.quotedCount"
                  class="supplier-cell__total"
                >
                  Índice {{ data.item.priceIndex.toFixed(2) }} ·
                  {{ data.item.quotedCount }}/{{ metadataFields.length - 1 }} materiales
                </div>
                <div
                  v-else
                  class="supplier-cell__total supplier-cell__total--empty"
                >
                  Sin cotizar
                </div>
              </template>
              <template v-else>
                <div
                  v-if="data.value && data.value.isAvailable"
                  class="comparative-price-cell"
                  :class="{
                    'comparative-price-cell--winner': data.value.isWinner,
                    'comparative-price-cell--lowest': data.value.isLowest && !data.value.isWinner,
                  }"
                >
                  <div class="comparative-price-cell__value">
                    $ {{ data.value.unitPrice | money }} MXN
                  </div>
                  <div
                    v-if="data.value.isWinner || data.value.isLowest"
                    :class="['comparative-price-cell__flag', {
                      'comparative-price-cell__flag--lowest': !data.value.isWinner,
                    }]"
                  >
                    {{ data.value.isWinner ? 'Ganador' : 'Menor precio' }}
                  </div>
                  <div
                    v-if="data.value.lastPriceUpdatedAt"
                    class="comparative-price-cell__updated"
                  >
                    <feather-icon
                      icon="ClockIcon"
                      size="12"
                      class="mr-25"
                    />
                    {{ formatLastPriceUpdate(data.value.lastPriceUpdatedAt) }}
                  </div>
                  <div
                    v-else
                    class="comparative-price-cell__updated comparative-price-cell__updated--empty"
                  >
                    Sin actualizacion registrada
                  </div>
                </div>
                <div
                  v-else
                  class="comparative-price-cell__na"
                  :class="{ 'comparative-price-cell__na--not-handled': data.value && data.value.isNotHandled }"
                >
                  {{ data.value && data.value.isNotHandled ? 'NA' : '-' }}
                </div>
              </template>
            </template>
          </b-table>

        </b-card>
      </b-col>
    </b-row>
    <b-row class="">
      <b-col>
        <b-card
          class="overflow-hidden"
          no-body
        >
          <b-table
            :no-border-collapse="true"
            responsive
            :items="quote.supplier_branches_shippings"
            :fields="shippingsFields"
            class="mb-0"
          >
            <!-- We are using utility class `text-nowrap` to help illustrate horizontal scrolling -->
            <template #head(supplier)="row">
              <div class="text-nowrap">
                {{ row.label }}
              </div>
            </template>
            <template #cell(range)="data">
              <div
                v-for="(ship, i) in data.item.shippings"
                :key="i"
              >
                <div :class="{ 'text-primary': ship.selected }">
                  <span v-if="ship.selected">{{ $t('selected') }}:</span>
                  <div>$ {{ ship.min }} - ${{ ship.max }}</div>
                  <div>{{ $t('price') }}: ${{ ship.price }}</div>
                </div>
              </div>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col>
        <client-pricing-list
          :quote="quote"
          :products="quote.quote_items_attributes"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ClientPricingList from '@/components/pricing/ClientPricingList.vue'
import QuotesTable from '@/components/pricing/QuotesTable.vue'
import CommissionSuggestionPanel from '@/components/pricing/CommissionSuggestionPanel.vue'

export default {
  components: {
    ClientPricingList,
    QuotesTable,
    CommissionSuggestionPanel,
  },

  data() {
    return {
      loading: false,
      openQuotationChoice: 'closed',
      savingOpenQuotation: false,
      quoteFields: [
        {
          key: 'folio',
          label: this.$t('folio'),
        },
        {
          key: 'is_invoiced',
          label: this.$t('isInvoiced'),
        },
        {
          key: 'is_paid',
          label: this.$t('isPaid'),
        },
        {
          key: 'is_published',
          label: this.$t('isPublished'),
        },
        {
          key: 'is_shipped',
          label: this.$t('isShipped'),
        },
        {
          key: 'quote_status',
          label: this.$t('quoteStatus'),
        },
      ],
      shippingsFields: [
        {
          key: 'name',
          label: this.$t('name'),
        },
        {
          key: 'range',
          label: this.$t('shippings'),
        },
      ],
      metadataItems: [],
      metadataFields: [],
      chartdata: {
        labels: ['Prov 1', 'Prov2', 'Prov 3', 'Prov 4'],
        datasets: [
          {
            label: this.$t('prices'),
            fill: false,
            borderColor: '#bb81fc',
            pointBackgroundColor: '#bb81fc',
            data: [10, 13, 8, 5],
            tension: 0,
            pointRadius: 3,
            pointBorderWidth: 9,
            spanGaps: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        // scales: {
        //   yAxes: {
        //     ticks: {
        //       stepSize: 5,
        //     },
        //   },
        // },
        scales: {
          yAxes: [
            {
              ticks: {
                display: false,
                stepSize: 0,
              },
            },
          ],
        },
      },
    }
  },

  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('products', {
      categories: 'productsGroupedByCategory',
      products: 'products',
    }),
    ...mapGetters('quotes', ['quote']),
    ...mapGetters('app', ['currentBreakPoint', 'isSmallScreen']),

    openQuotationOptions() {
      return [
        { value: 'open', text: 'Abierta' },
        { value: 'closed', text: 'Cerrada' },
      ]
    },

    showCloserTraceability() {
      return this.currentUser?.role_name === 'admin' && Boolean(
        this.quote?.quote_closer_name
        || this.quote?.quote_closer_email
        || this.quote?.quote_closer_origin,
      )
    },

    closerOriginLabel() {
      const origin = this.quote?.quote_closer_origin
      if (origin === 'admin') return this.$t('quoteCreatorTypeAdmin')
      if (origin === 'customer') return this.$t('quoteCreatorTypeCustomer')
      return origin || this.$t('notAvailable')
    },

    closerClosedAtLabel() {
      const closedAt = this.quote?.quote_closed_at
      if (!closedAt) return this.$t('notAvailable')

      const date = new Date(closedAt)
      if (Number.isNaN(date.getTime())) return this.$t('notAvailable')

      const locale = this.$i18n?.locale || 'es-MX'
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    },
  },
  created() {
    this.loading = true
    this.fetchQuoteMetadata(this.$route.params.id).then(response => {
      const quote = { ...response }
      const branchPrices = quote.supplier_branches_comparative
      const fields = branchPrices.slice(1).map(row => ({
        key: row[0].id,
        label: row[0].name,
        tdClass: 'text-center align-middle',
        thClass: 'text-center align-middle text-wrap',
        thStyle: { width: '20%' },
      }))
      fields.unshift({
        key: 'supplier',
        label: this.$t('supplier'),
        stickyColumn: true,
        isRowHeader: true,
        thStyle: { width: '20%' },
        thClass: 'align-middle',
      })
      const branchPricesT = this.transpose(quote.supplier_branches_comparative)
      // const items = branchPricesT.slice(1).map((row, index)=>({supplier: row[0].name, supplierId: row[0].id, fields[index+1].id:row }))
      const items = []
      for (let i = 1; i < branchPricesT.length; i += 1) {
        const item = {
          supplier: branchPricesT[i][0].name,
          supplierId: branchPricesT[i][0].id,
        }
        for (let j = 1; j < branchPricesT[i].length; j += 1) {
          item[fields[j].key] = this.buildComparativeCell(branchPricesT[i][j])
        }
        items.push(item)
      }
      this.flagWinningPrices(fields, items, quote.quote_items_attributes)
      this.rankSuppliersByPriceIndex(fields, items)
      this.metadataFields = fields
      this.metadataItems = items
      this.syncVisibilityState(quote)
      this.loading = false
    })
  },
  methods: {
    ...mapActions('quotes', ['fetchQuoteMetadata', 'updateQuoteOpenQuotation']),

    // /checkout/:quoteId es público: el cliente la abre sin cuenta y puede pagar ahí mismo.
    async copyClientLink() {
      const url = `${window.location.origin}/checkout/${this.$route.params.id}`

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url)
        } else {
          const field = document.createElement('textarea')
          field.value = url
          field.setAttribute('readonly', '')
          field.style.position = 'fixed'
          field.style.opacity = '0'
          document.body.appendChild(field)
          field.select()
          document.execCommand('copy')
          document.body.removeChild(field)
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('clientLinkCopied'), text: url, icon: 'LinkIcon', variant: 'success',
          },
        })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('clientLinkCopyFailed'), text: url, icon: 'AlertCircleIcon', variant: 'warning',
          },
        })
      }
    },

    choiceFromQuote(quote) {
      return quote?.open_quotation_enabled ? 'open' : 'closed'
    },

    syncVisibilityState(quote) {
      this.openQuotationChoice = this.choiceFromQuote(quote)
    },

    async saveOpenQuotation(choice) {
      const values = { open: true, closed: false }
      // El v-model ya cambió cuando llega el @change, así que el valor al que hay que
      // regresar en caso de error se saca de lo que está guardado en la cotización.
      const previous = this.choiceFromQuote(this.quote)
      this.savingOpenQuotation = true

      try {
        await this.updateQuoteOpenQuotation({
          quoteId: this.$route.params.id,
          openQuotation: values[choice],
        })
        this.openQuotationChoice = choice
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.quote.open_quotation_enabled
              ? 'La cotización quedó ABIERTA para el cliente.'
              : 'La cotización quedó CERRADA para el cliente.',
            icon: 'EyeIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        this.openQuotationChoice = previous
      } finally {
        this.savingOpenQuotation = false
      }
    },

    // Ganador = el proveedor que se quedó con la partida. Se marca aparte del menor precio
    // porque no siempre coinciden: el flete y el crédito mueven la decisión.
    flagWinningPrices(fields, items, quoteItems) {
      const winnerBySupplierColumn = {}
      const list = Array.isArray(quoteItems) ? quoteItems : []

      list.forEach(quoteItem => {
        if (!quoteItem?.id || !quoteItem?.selected_supplier_branch_id) return
        winnerBySupplierColumn[quoteItem.id] = String(quoteItem.selected_supplier_branch_id)
      })

      fields.slice(1).forEach(field => {
        const available = items
          .map(item => item[field.key])
          .filter(cell => cell && cell.isAvailable)

        if (!available.length) return

        const lowestPrice = Math.min(...available.map(cell => cell.unitPrice))

        items.forEach(item => {
          const cell = item[field.key]
          if (!cell || !cell.isAvailable) return

          cell.isLowest = cell.unitPrice === lowestPrice
          cell.isWinner = winnerBySupplierColumn[field.key] === String(item.supplierId)
        })
      })
    },

    // Filas de proveedor del más competitivo al menos competitivo. No se puede usar el total
    // cotizado: cada proveedor cotiza partidas distintas, y el que cotiza dos sale hasta
    // arriba solo por eso. En su lugar, por cada material se mide el P.U. contra el más
    // barato de esa partida y se promedia; 1.00 es haber ganado todo lo que coticó.
    rankSuppliersByPriceIndex(fields, items) {
      const lowestByMaterial = {}

      fields.slice(1).forEach(field => {
        const prices = items
          .map(item => item[field.key])
          .filter(cell => cell && cell.isAvailable)
          .map(cell => cell.unitPrice)

        if (prices.length) lowestByMaterial[field.key] = Math.min(...prices)
      })

      items.forEach(item => {
        const ratios = []

        fields.slice(1).forEach(field => {
          const cell = item[field.key]
          const lowest = lowestByMaterial[field.key]
          if (!cell || !cell.isAvailable || !lowest) return

          ratios.push(cell.unitPrice / lowest)
        })

        item.quotedCount = ratios.length
        item.priceIndex = ratios.length
          ? ratios.reduce((sum, ratio) => sum + ratio, 0) / ratios.length
          : null
      })

      items.sort((a, b) => {
        if (a.priceIndex === null || b.priceIndex === null) {
          return a.priceIndex === null ? 1 : -1
        }
        if (a.priceIndex !== b.priceIndex) return a.priceIndex - b.priceIndex

        return b.quotedCount - a.quotedCount
      })
    },

    buildComparativeCell(cell) {
      const isNotHandled = cell?.catalog_status === 'not_handled'
      const rawUnitPrice = cell?.unit_price
      const hasUnitPrice = rawUnitPrice !== null && rawUnitPrice !== undefined && rawUnitPrice !== ''
      const unitPrice = hasUnitPrice ? Number(rawUnitPrice) : null
      const isAvailable = Number.isFinite(unitPrice) && unitPrice > 0

      if (!isAvailable) {
        return {
          isAvailable: false,
          isNotHandled,
          unitPrice: null,
          lastPriceUpdatedAt: null,
          isWinner: false,
          isLowest: false,
        }
      }

      return {
        isAvailable: true,
        isNotHandled: false,
        unitPrice,
        lastPriceUpdatedAt: cell?.last_price_updated_at || null,
        isWinner: false,
        isLowest: false,
      }
    },

    formatLastPriceUpdate(isoDate) {
      if (!isoDate) return ''
      const date = new Date(isoDate)
      if (Number.isNaN(date.getTime())) return 'Actualizado: -'

      const locale = this.$i18n?.locale || 'es-MX'
      const formattedDate = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)

      return `Actualizado: ${formattedDate}`
    },

    transpose(mx) {
      const h = mx[0].length
      const w = mx.length
      // const matrix = [...mx]
      // const matrixT = Array(w).fill([])
      const t = []
      for (let i = 0; i < h; i += 1) {
        // Insert a new row (array)
        t[i] = []

        // Loop through every item per item in outer array (width)
        for (let j = 0; j < w; j += 1) {
          // Save transposed data.
          t[i][j] = mx[j][i]
        }
      }
      return t
    },
  },
}
</script>

<style lang="scss" scoped>
.supplier-cell {
  font-weight: 600;
  color: #4b4b63;
}

.supplier-cell__total {
  font-size: 0.72rem;
  color: #6e6b7b;
  font-variant-numeric: tabular-nums;
  margin-top: 2px;
}

.supplier-cell__total--empty {
  color: #b2aebf;
}

.comparative-price-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1.2;
}

.comparative-price-cell__value {
  font-weight: 700;
  color: #25233a;
  font-variant-numeric: tabular-nums;
}

.comparative-price-cell__updated {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  color: #6e6b7b;
}

.comparative-price-cell__updated--empty {
  color: #b2aebf;
}

.comparative-price-cell__na {
  font-weight: 600;
  color: #b2aebf;
}

.comparative-price-cell__na--not-handled {
  color: #6e6b7b;
  letter-spacing: 0.35px;
}

.comparative-price-cell--winner {
  background-color: rgba(40, 199, 111, 0.16);
  border-radius: 0.4rem;
  padding: 0.35rem 0.25rem;
}

.comparative-price-cell--winner .comparative-price-cell__value {
  color: #1f9d63;
}

/* Gris y no un verde tenue: el verde es del ganador, y dos verdes de distinta
   intensidad se confunden entre si. */
.comparative-price-cell--lowest {
  background-color: rgba(110, 107, 123, 0.15);
  border-radius: 0.4rem;
  padding: 0.35rem 0.25rem;
}

.comparative-price-cell__flag {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #1f9d63;
}

.comparative-price-cell__flag--lowest {
  color: #5e5a6e;
}

.comparative-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #ebe9f1;
}

/* El encabezado de materiales y la columna de proveedor quedan fijos al hacer scroll. */
.comparative-prices-table {
  ::v-deep thead th {
    position: sticky;
    top: 0;
    z-index: 3;
    background-color: #f3f2f7;
  }

  ::v-deep .b-table-sticky-column {
    z-index: 2;
    background-color: #fff;
  }

  ::v-deep thead th.b-table-sticky-column {
    z-index: 4;
    background-color: #f3f2f7;
  }
}

.quote-visibility-card__badge {
  font-size: 0.8rem;
  letter-spacing: 0.5px;
}

@media (max-width: 767px) {
  .comparative-price-cell__value {
    font-size: 0.85rem;
  }

  .comparative-price-cell__updated {
    font-size: 0.68rem;
  }
}
</style>
